<template>
  <!-- Кнопка "Оплатить" для неоплаченных заказов -->
  <div 
    v-if="shouldShow" 
    class="retry-payment-section"
    :class="{ 'expired': !canRetryPayment }"
  >
    <UiKitButton
      color="warning"
      variant="shadow"
      size="lg"
      radius="lg"
      full-width
      :disabled="isRetryingPayment || isInitiatingPayment || !canRetryPayment"
      :loading="isRetryingPayment || isInitiatingPayment"
      @click="handleRetryPayment"
    >
      <template #leading>
        <UIcon name="i-heroicons-credit-card" class="h-5 w-5 shrink-0" />
      </template>
      {{ retryPaymentButtonLabel }}
    </UiKitButton>

    <!-- Кнопка создания нового заказа для истекших -->
    <UiKitButton
      v-if="!canRetryPayment"
      color="default"
      variant="flat"
      size="lg"
      radius="lg"
      full-width
      @click="handleCreateNewOrder"
    >
      Создать новый заказ
    </UiKitButton>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { shouldShowRetryPayment } from '@entities/order'
import type { Order } from '@entities/order'

interface Props {
  order: Order | null
  canRetryPayment: boolean
  isRetryingPayment: boolean
  isInitiatingPayment: boolean
  formattedTimeRemaining: string | null
  paymentUrl: string | null
}

interface Emits {
  (e: 'retryPayment'): void
  (e: 'createNewOrder'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

/**
 * Проверяет, должен ли показываться блок повторной оплаты
 */
const shouldShow = computed(() => {
  return shouldShowRetryPayment(props.order)
})

/**
 * Получает текст для кнопки повторной оплаты в зависимости от состояния
 */
const retryPaymentButtonLabel = computed(() => {
  if (props.isRetryingPayment || props.isInitiatingPayment) {
    return 'Подготовка оплаты...'
  }
  if (!props.canRetryPayment) {
    return '⏰ Время истекло'
  }
  return 'Оплатить заказ'
})

/**
 * Обрабатывает нажатие на кнопку повторной оплаты
 */
function handleRetryPayment(): void {
  if (!props.canRetryPayment || props.isRetryingPayment || props.isInitiatingPayment) return
  emit('retryPayment')
}

/**
 * Обрабатывает нажатие на кнопку создания нового заказа
 */
function handleCreateNewOrder(): void {
  emit('createNewOrder')
}
</script>

<style scoped lang="scss">
.retry-payment-section {
  @apply text-center flex flex-col items-center gap-3;

  &.expired {
    @apply bg-hui-danger/10 border border-hui-danger/30 p-4 rounded-hui-lg;
  }
}

.retry-payment-text {
  @apply text-[0.9rem] text-gray-600 dark:text-gray-300 m-0;
}

.retry-payment-timer {
  @apply text-[0.85rem] text-orange-600 dark:text-orange-400 mt-2 mb-0 font-medium;
}

.retry-payment-expired-timer {
  @apply text-[0.85rem] text-red-600 dark:text-red-400 mt-2 mb-3 font-semibold bg-red-50 dark:bg-red-900/20 px-3 py-1 rounded-md border border-red-200 dark:border-red-700;
}

.retry-payment-expired-text {
  @apply text-[0.9rem] text-red-600 dark:text-red-400 mb-3;
}
</style>